import { getAuth } from 'firebase/auth'

const BASE_URL = import.meta.env.VITE_API_URL || '/api'

async function getAuthToken(): Promise<string | null> {
  const auth = getAuth()
  const user = auth.currentUser
  if (!user) return null
  return user.getIdToken()
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAuthToken()

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  if (options.body && typeof options.body === 'string') {
    headers['Content-Type'] = 'application/json'
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    const message = error.error || error.message || `Request failed: ${response.status}`
    throw new Error(`${response.status}: ${message}`)
  }

  return response.json()
}

export const api = {
  get: <T>(path: string, params?: Record<string, string>) => {
    const url = params
      ? `${path}?${new URLSearchParams(params).toString()}`
      : path
    return request<T>(url)
  },

  post: <T>(path: string, body: unknown) => {
    return request<T>(path, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },

  patch: <T>(path: string, body: unknown) => {
    return request<T>(path, {
      method: 'PATCH',
      body: JSON.stringify(body),
    })
  },

  delete: <T>(path: string) => {
    return request<T>(path, { method: 'DELETE' })
  },

  uploadFile: async <T>(path: string, formData: FormData): Promise<T> => {
    const token = await getAuthToken()

    const headers: Record<string, string> = {}
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    // Don't set Content-Type - let browser set it with boundary for multipart/form-data

    const response = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      headers,
      body: formData,
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      const message = error.error || error.message || `Upload failed: ${response.status}`
      throw new Error(`${response.status}: ${message}`)
    }

    return response.json()
  },
}
